import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer, springPresets } from '@/lib/motion';
import { Button } from '@/components/ui/button';

const reviews = [
  {
    id: 1,
    name: "Anna K.",
    rating: 5,
    text: "Rachela pomogła mi przejść przez bardzo trudny okres w moim życiu. Jej profesjonalizm i empatia sprawiły, że poczułam się bezpiecznie i zrozumiana. Polecam z całego serca.",
    service: "Konsultacje psychologiczne"
  },
  {
    id: 2,
    name: "Marcin i Joanna",
    rating: 5,
    text: "Dzięki terapii par u Racheli udało nam się odbudować komunikację w naszym związku. Jej podejście jest bardzo praktyczne i skuteczne. Jesteśmy wdzięczni za jej pomoc.",
    service: "Terapia par"
  },
  {
    id: 3,
    name: "Tomasz P.",
    rating: 5,
    text: "Profesjonalne podejście, konkretne narzędzia i widoczne efekty. Rachela potrafi w prosty sposób wytłumaczyć skomplikowane mechanizmy psychologiczne. Bardzo polecam.",
    service: "Psychoterapia CBT"
  },
  {
    id: 4,
    name: "Magdalena S.",
    rating: 5,
    text: "Konsultacje seksuologiczne u Racheli były dla mnie przełomowe. Atmosfera pełna zrozumienia i brak osądzania pozwoliły mi otworzyć się na zmiany. Dziękuję!",
    service: "Konsultacje seksuologiczne"
  },
  {
    id: 5,
    name: "Firma ABC Sp. z o.o.",
    rating: 5,
    text: "Rachela prowadziła mediację w naszym sporze biznesowym. Jej profesjonalizm i umiejętność znalezienia kompromisu pomogły nam osiągnąć porozumienie korzystne dla obu stron.",
    service: "Mediacje biznesowe"
  },
  {
    id: 6,
    name: "Katarzyna M.",
    rating: 5,
    text: "Szkolenie z komunikacji prowadzone przez Rachelę było bardzo praktyczne i inspirujące. Zdobyta wiedza pomaga mi codziennie w pracy z zespołem. Gorąco polecam!",
    service: "Szkolenia"
  }
];

const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-5 h-5 ${
            i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

export default function Reviews() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl lg:text-7xl font-serif text-foreground mb-6"
            >
              Opinie <span className="text-accent">klientów</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-muted-foreground leading-relaxed mb-8"
            >
              Poznaj doświadczenia osób, które zdecydowały się na współpracę ze mną. 
              Każda opinia to historia zmiany i rozwoju osobistego.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Umów wizytę <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                variants={fadeInUp}
                custom={index}
                className="bg-background rounded-2xl p-8 shadow-lg border border-border hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <Quote className="w-8 h-8 text-accent/60" />
                  <StarRating rating={review.rating} />
                </div>
                
                <p className="text-muted-foreground leading-relaxed mb-6 text-sm">
                  "{review.text}"
                </p>
                
                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-foreground">{review.name}</p>
                      <p className="text-xs text-muted-foreground">{review.service}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl lg:text-4xl font-serif text-foreground mb-6"
            >
              Dołącz do grona zadowolonych klientów
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-muted-foreground leading-relaxed mb-8"
            >
              Każda historia zmiany zaczyna się od pierwszego kroku. 
              Zapraszam do kontaktu i wspólnego odkrywania Twojego potencjału.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Umów pierwszą konsultację <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}